import {safeText} from "../../js/util/CommonUtils.js";
import {createOrgNameUrlTemplate, extractOrgNameFromUrl} from "../../js/util/UrlUtils.js";
import BreadcrumbsHeaderCmp from "./BreadcrumbsHeaderCmp.js";

export default class BreadcrumbsHeaderHtml {
    /** @type {HTMLElement} */
    #list;
    #orgLink = createOrgNameUrlTemplate(extractOrgNameFromUrl())

    /** @param {HTMLElement} parent */
    init(parent){
        this.#createList(parent)
    }

    /** @param {string} orgName */
    addOrg(orgName){
        const orgEl = this.getElementByDataId(BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.ORG);
        if (orgEl)
            orgEl.remove()
        this.#list.insertAdjacentHTML('beforeend', this.#orgTemplate(orgName))
    }

    /** @param {string} orgName */
    addBatchList(orgName){
        // We should remove orgEl, because in batch case instead of an organization (and /injections) we add link to batchList
        const orgEl = this.getElementByDataId(BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.ORG);
        if (orgEl)
            orgEl.remove()
        const batchListEl = this.getElementByDataId(BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.BATCH_LIST);
        if (batchListEl)
            batchListEl.remove()
        this.#list.insertAdjacentHTML('beforeend', this.#batchListTemplate(orgName))
    }

    /** @param {string} orgName */
    addPostList(orgName){
        // We should remove orgEl, because in post case instead of an organization (and /injections) we add link to postList
        const orgEl = this.getElementByDataId(BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.ORG);
        if (orgEl)
            orgEl.remove()
        const postListEl = this.getElementByDataId(BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.POST_LIST);
        if (postListEl)
            postListEl.remove()
        this.#list.insertAdjacentHTML('beforeend', this.#postListTemplate(orgName))
    }

    /** @param {string} injectionName */
    addInjectionName(injectionName){
        const injEl = this.getElementByDataId(BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.INJECTION);
        if (injEl)
            injEl.remove()
        this.#list.insertAdjacentHTML('beforeend', this.#injectionTemplate(injectionName))
    }

    /**
     * @param {string} batchName
     * @param {string} batchId
     */
    addBatchName(batchName, batchId){
        const batchEl = this.getElementByDataId(BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.BATCH);
        if (batchEl)
            batchEl.remove()
        this.#list.insertAdjacentHTML('beforeend', this.#batchTemplate(batchName, batchId))
    }

    addSettingsName(){
        const settingsEl = this.getElementByDataId(BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.SETTINGS);
        if (settingsEl)
            settingsEl.remove()
        this.#list.insertAdjacentHTML('beforeend', this.#settingsTemplate())
    }

    /** @param {{name: string, breadcrumbsName: string}} screenMode */
    addSettingsScreenName(screenMode) {
        const screenEl = this.getElementByDataId(BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.SETTINGS_SCREEN);
        if (screenEl)
            screenEl.remove()
        const teamEl = this.getElementByDataId(BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.TEAM);
        if (teamEl)
            teamEl.remove()
        this.#list.insertAdjacentHTML('beforeend', this.#settingsScreenTemplate(screenMode.breadcrumbsName, screenMode.name))
    }

    /** @param {string} name */
    addTeamName(name) {
        const teamEl = this.getElementByDataId(BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.TEAM);
        if (teamEl)
            teamEl.remove()
        this.#list.insertAdjacentHTML('beforeend', this.#teamTemplate(name))
    }

     /** @param {Post} post */
    addPostTitle(post) {
        const postEl = this.getElementByDataId(BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.POST);
        if (postEl)
            postEl.remove()
        this.#list.insertAdjacentHTML('beforeend', this.#postTemplate(post))
    }

    /** @param {string} hash */
    addHashToBatchUrl(hash){
        const batchLink = this.getElementByDataId(BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.BATCH_LINK);
        if (batchLink){
            let url = batchLink.getAttribute('href');
            url = url + hash;
            batchLink.setAttribute('href', url)
        }
    }

    /** @param {string?} text */
    addEditItem(text){
        this.#list.insertAdjacentHTML('beforeend', this.#editTemplate(text))
    }

    getElementByDataId(name) {
        return this.#list.querySelector(`[data-id="${name}"]`)
    }

    /** @param {HTMLElement} parent */
    #createList(parent) {
        const list = document.createElement('ul');
        list.classList.add('breadcrumbs');
        parent.insertAdjacentElement('beforeend', list);
        this.#list = list;
    }

    /**
     * @param {string} orgName
     * @returns {string}
     */
    #orgTemplate(orgName){
        return `<li data-id="${BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.ORG}" class="breadcrumbs__item">
                    <a class="breadcrumbs__link breadcrumbs__org-link" href="${this.#orgLink}">${orgName}</a>
                    <span data-id="${BreadcrumbsHeaderCmp.MENU_ID.MENU_ORG}" class="breadcrumbs__menu-icon"></span>
                </li>`
    }

    /** @returns {string} */
    #batchListTemplate(orgName){
        return `<li data-id="${BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.BATCH_LIST}" class="breadcrumbs__item">
                    <a class="breadcrumbs__link breadcrumbs__batch-list-link" href="${this.#orgLink}/batches">${orgName}</a>
                </li>`
    }

    /** @returns {string} */
    #postListTemplate(orgName){
        return `<li data-id="${BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.POST_LIST}" class="breadcrumbs__item">
                    <a class="breadcrumbs__link breadcrumbs__batch-list-link" href="${this.#orgLink}/posts">${orgName}</a>
                </li>`
    }

    /**
     * @param {string} batchName
     * @param {string} batchId
     * @returns {string}
     */
    #batchTemplate(batchName, batchId){
        return `<li data-id="${BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.BATCH}" class="breadcrumbs__item">
                    <a class="breadcrumbs__link breadcrumbs__batch-link" data-id="${BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.BATCH_LINK}" href="${this.#orgLink}/batch/${batchId}">${safeText(batchName)}</a>
                    <span data-id="${BreadcrumbsHeaderCmp.MENU_ID.MENU_BATCH}" class="breadcrumbs__menu-icon"></span>
                </li>`
    }

    /**
     * @param {string?} text
     * @returns {string}
     */
    #editTemplate(text){
        const title = safeText(text) || 'Edit';
        return `<li data-id="${BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.ITEM_EDIT}" class="breadcrumbs__item">${title}</li>`
    }

    /**
     * @param {string} injectionName
     * @returns {string}
     */
    #injectionTemplate(injectionName ){
        return `<li data-id="${BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.INJECTION}" class="breadcrumbs__item">
                    <a class="breadcrumbs__link breadcrumbs__injection-link" href="#">${safeText(injectionName)}</a>
                    <span data-id="${BreadcrumbsHeaderCmp.MENU_ID.MENU_INJECTION}" class="breadcrumbs__menu-icon"></span>
                </li>`
    }

    /** @returns {string} */
    #settingsTemplate(){
        return `<li data-id="${BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.SETTINGS}" class="breadcrumbs__item">
                    <a class="breadcrumbs__link breadcrumbs__settings-link" href="${this.#orgLink}/settings">Settings</a>
                </li>`
    }

    /**
     * @param {string} itemName
     * @param {string} urlParam
     * @returns {string}
     */
    #settingsScreenTemplate(itemName, urlParam){
        return `<li data-id="${BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.SETTINGS_SCREEN}" class="breadcrumbs__item">
                    <a class="breadcrumbs__link breadcrumbs__settings-screen-link" href="${this.#orgLink}/settings?screen=${urlParam}">${safeText(itemName)}</a>
                </li>`
    }

    /**
     * @param {string} name
     * @returns {string}
     */
    #teamTemplate(name){
        return `<li data-id="${BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.TEAM}" class="breadcrumbs__item">
                    <a class="breadcrumbs__link breadcrumbs__team-link" href="#">${safeText(name)}</a>
                </li>`
    }

    /**
     * @param {Post} post
     * @returns {string}
     */
    #postTemplate(post){
        return `<li data-id="${BreadcrumbsHeaderCmp.BREADCRUMBS_ITEM_ID.POST}" class="breadcrumbs__item">
                    <a class="breadcrumbs__link breadcrumbs__post-link" href="${this.#orgLink}/post/${post.id}"">${safeText(post.title)}</a>
                    <span data-id="${BreadcrumbsHeaderCmp.MENU_ID.MENU_POST}" class="breadcrumbs__menu-icon"></span>
                </li>`
    }
}